import { Injectable } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { StringUtilities } from "@common/lib/utilities/string-utilities";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { from, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { ISearchProviderOptions, SearchType } from "../search.interface";
import { SearchProvider } from "../search-provider";
import { ISearchResultMatch, ITeamSearchResult, PersonTeamRoleResultType } from "../search-results.interface";

@Injectable()
export class TeamSearchProvider extends SearchProvider<ITeamSearchResult> {
    public readonly Type = SearchType.PersonTeamRole;

    public teams: Team[] = [];

    public constructor(private teamsService: CommonTeamsService) {
        super();
    }

    public shouldSkip(options: ISearchProviderOptions): boolean {
        return !!options.teamId || !!options.personId || !!options.updatedSince;
    }

    public initialise() {
        return from(this.teamsService.promiseToGetAllTeams()).pipe(
            // prime team supp data so we can "search" accountabilities
            switchMap((teams) => this.teamsService.getAllTeamSupplementaryData().pipe(
                map(() => teams),
            )),
            tap((teams: Team[]) => this.teams = teams),
        );
    }

    public execute({ keyword, activeOnly, labelIds }: ISearchProviderOptions) {
        const hasLabels = !!labelIds && labelIds.size > 0;
        return of(this.teams
            .filter((t) => !activeOnly || t.isActive())
            .filter((t) => !hasLabels || t.labelLocations.some((ll) => labelIds?.has(ll.labelId)))
            .filter((t) => !keyword
                || this.hasKeyword(t?.name, keyword)
                || this.hasKeyword(t.supplementaryData?.accountabilities, keyword))
            .slice(0, 10)
            .map((team) => {
                const results: ISearchResultMatch[] = [];

                if (this.hasKeyword(team.supplementaryData?.accountabilities, keyword)) {
                    results.push({
                        field: ["Accountabilities"],
                        snippet: StringUtilities.generateSnippet(team.supplementaryData?.accountabilities ?? "", keyword),
                    });
                }

                return ({ type: PersonTeamRoleResultType.Team, team, results } as ITeamSearchResult);
            }));
    }
}
